import { useCallback, useEffect, useMemo, useState, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { usePreviaPisCofins } from "../PreviaPisCofinsContext";
import TabelaGrupos from "../../../components/PisCofins/SobDespesas/TabelaGrupos";
import Loading from "../../../components/Loading";
import SnackBar from "../../../components/SnackBar";
import { buscarConciliacaoSpedContas, buscarMotorAgrupado, buscarMotorDetalhes, buscarConciliacaoSpedLancamentos, atualizarDetalheLancamento, atualizarDetalhesConta, buscarPreviaVersaoPisCofins, buscarTotaisCompoeConciliacaoSped, exportarCsvPreviaPisCofins } from "../../../services/api-piscofins.service";
import { buscarPlanTribBaseLegal } from "../../../services/api-planejamento-tributario.service";
import BlueButton from "../../../components/BlueButton";
import WhiteButton from "../../../components/WhiteButton";
import Pagination from "../../../components/Pagination";
import { formatValue } from "../../../utils/formatValue";
import FiltroTabelaPisCofins from "../../../components/PisCofins/FiltroTabelaPisCofins";
import StepsIndicator from "../../../components/PisCofins/StepsIndicator";
import { useFilaAtualizacoesGrupos } from "../../../hooks/useFilaAtualizacoesGrupos";
import DownloadIcon from "@mui/icons-material/Download";
import RefreshIcon from "@mui/icons-material/Refresh";
import styles from "./styles.module.css";
import {
  usePermissoes,
  CHAVES_PIS_COFINS_EDIT,
  CHAVES_PIS_COFINS_EXPORT,
} from "../../../config/Context/PermissoesContext";

const SobDespesas = () => {
  const { pode } = usePermissoes();
  const navigate = useNavigate();
  const { versaoId, script } = useParams();
  const {
    dadosVersao,
    versaoSelecionada,
    isProjetoIniciado,
    setPreviaInfo,
    previaInfo,
    voltarParaListaComModalVersoes,
  } = usePreviaPisCofins();
  
  const [loading, setLoading] = useState(false);
  const [dadosAgrupados, setDadosAgrupados] = useState(null);
  const [objSnackBar, setObjSnackBar] = useState({ type: "", msg: "" });
  const [basesLegais, setBasesLegais] = useState([]);
  const [loadingBasesLegais, setLoadingBasesLegais] = useState(false);
  const [colunaFiltro, setColunaFiltro] = useState("");
  const [valorFiltro, setValorFiltro] = useState("");
  const [paginaAtual, setPaginaAtual] = useState(1);
  const [limitePorPagina, setLimitePorPagina] = useState(10);
  const [gruposSalvando, setGruposSalvando] = useState(new Set());
  // ✅ Estados para drill down (chave = schema_empresa|id_conta_contabil para exibir apenas lançamentos daquele schema+conta)
  const [contaExpandida, setContaExpandida] = useState(null); // Chave do grupo expandido: schema_empresa|id_conta_contabil
  const [lancamentosDetalhados, setLancamentosDetalhados] = useState({}); // { [chaveGrupo]: { lancamentos, pagina, temMais, total, schema_empresa, contaContabil, id_conta_contabil } }
  const [carregandoLancamentos, setCarregandoLancamentos] = useState(new Set());
  const lancamentosDetalhadosRef = useRef({}); // Ref para acessar estado atual
  const buscandoPorDescricaoRef = useRef(false); // Ref para evitar múltiplas buscas simultâneas
  const ultimaBuscaDescricaoRef = useRef(""); // Ref para rastrear a última busca realizada
  // ✅ Estados para filtro de descrição de lançamento
  const [lancamentosFiltrados, setLancamentosFiltrados] = useState(new Set()); // Set de linhaIndex dos lançamentos que correspondem ao filtro
  const [contasComFiltro, setContasComFiltro] = useState(new Set()); // Set de contas que têm lançamentos correspondentes
  // Fonte primária para empresa/CNPJ: resposta da API por versaoId (evita dados de outro CNPJ quando contexto está desatualizado)
  const [dadosPreviaVersaoApi, setDadosPreviaVersaoApi] = useState(null);

  // Derivar versaoIdFinal: prioridade URL, fallback contexto. Aceitar 0 como válido; inválido apenas undefined, null ou NaN.
  const versaoIdFromUrl = versaoId != null && String(versaoId).trim() !== "" ? Number(versaoId) : undefined;
  const idFromUrlValid = versaoIdFromUrl !== undefined && !Number.isNaN(versaoIdFromUrl);
  const versaoIdFromContext = dadosVersao?.versaoId;
  const idFromContextValid =
    versaoIdFromContext !== undefined &&
    versaoIdFromContext !== null &&
    !Number.isNaN(Number(versaoIdFromContext));
  const versaoIdFinal = idFromUrlValid
    ? versaoIdFromUrl
    : idFromContextValid
      ? Number(versaoIdFromContext)
      : undefined;
  const versaoIdInvalido =
    versaoIdFinal === undefined || versaoIdFinal === null || Number.isNaN(versaoIdFinal);

  const scriptFinal = script || dadosVersao?.script || "CONCILIACAO_SPED";
  const totais = dadosAgrupados?.totais;

  // Verificar se a prévia está com status PROJETO-INICIADO (validação centralizada)
  const isBloqueada = isProjetoIniciado() || !pode(CHAVES_PIS_COFINS_EDIT);

  // Função para normalizar conta contábil (mesma lógica do backend) - definida antes para uso em carregarDadosAgrupados
  const normalizarConta = useCallback((conta) => {
    const value = String(conta ?? '').trim();
    return value || 'NÃO INFORMADO';
  }, []);

  const formatCurrency = (valor = 0) =>
    new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(valor);

  const formatarCnpj = (cnpj) => {
    if (!cnpj) return "Não informado";
    const cnpjLimpo = cnpj.replace(/\D/g, "");
    if (cnpjLimpo.length === 14) {
      return cnpjLimpo.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, "$1.$2.$3/$4-$5");
    }
    return cnpj;
  };

  const carregarDadosAgrupados = async (filtroDescricaoParam, filtroColunaParam, filtroValorParam) => {
    if (versaoIdInvalido || !scriptFinal) {
      console.warn('⚠️ [SobDespesas] Dados incompletos para carregar:', {
        versaoIdFinal,
        scriptFinal,
      });
      return;
    }

    setLoading(true);
    try {
      // Filtros: tipoContaContabil = contas_contabeis.tipo (texto do plano). tipoConta na resposta = classificação por dígito.
      const filtrosBackendColunas = ['descricaoConta', 'cnpj', 'codigoEcd', 'tipoContaContabil', 'compoeProjeto'];
      const filtroDescricaoFinal =
        filtroDescricaoParam !== undefined
          ? (filtroDescricaoParam && String(filtroDescricaoParam).trim() ? String(filtroDescricaoParam).trim() : undefined)
          : (colunaFiltro === "descricaoLancamento" && valorFiltro ? String(valorFiltro).trim() : undefined);
      const filtroColunaFinal =
        filtroColunaParam !== undefined
          ? (filtroColunaParam && filtrosBackendColunas.includes(filtroColunaParam) ? filtroColunaParam : undefined)
          : (filtrosBackendColunas.includes(colunaFiltro) ? colunaFiltro : undefined);
      const valorParaFiltro =
        filtroValorParam !== undefined
          ? filtroValorParam
          : (filtroColunaParam === undefined ? valorFiltro : undefined);

      let filtroValorFinal = undefined;
      if (filtroColunaFinal) {
        if (filtroColunaFinal === 'compoeProjeto' && (valorParaFiltro === true || valorParaFiltro === false)) {
          filtroValorFinal = valorParaFiltro;
        } else if (
          (filtroColunaFinal === 'tipoContaContabil' || filtroColunaFinal === 'tipoEcd') &&
          valorParaFiltro !== undefined &&
          valorParaFiltro !== null &&
          String(valorParaFiltro).trim() === ''
        ) {
          filtroValorFinal = '';
        } else if (
          valorParaFiltro !== undefined &&
          valorParaFiltro !== null &&
          String(valorParaFiltro).trim() !== ''
        ) {
          filtroValorFinal =
            typeof valorParaFiltro === 'string' ? String(valorParaFiltro).trim() : valorParaFiltro;
        }
      }

      let previaId = dadosVersao?.idPreviaPisCofins ?? previaInfo?.idPreviaPisCofins ?? previaInfo?.idPrevia;
      if (previaId == null || previaId === undefined) {
        const response = await buscarPreviaVersaoPisCofins(versaoIdFinal);
        previaId = response?.idPrevia ?? response?.previa?.idPreviaPisCofins;
      }
      if (previaId == null || previaId === undefined) {
        setObjSnackBar({ type: "error", msg: "Não foi possível obter o ID da prévia." });
        setLoading(false);
        return;
      }

      const resultado = await buscarConciliacaoSpedContas(
        previaId,
        versaoIdFinal,
        filtroDescricaoFinal,
        filtroColunaFinal,
        filtroValorFinal
      );

      if (resultado?.sucesso) {
        // ✅ Manter período no cabeçalho quando a API com filtro não retorna periodo
        const periodoPreservar = dadosAgrupados?.periodo ?? dadosVersao?.periodo ?? previaInfo?.periodo ?? null;
        const payload = {
          ...resultado,
          periodo: resultado.periodo ?? periodoPreservar ?? (dadosVersao?.dtInicial != null && dadosVersao?.dtFinal != null ? { dtInicial: dadosVersao.dtInicial, dtFinal: dadosVersao.dtFinal } : null) ?? (previaInfo?.dtInicial != null && previaInfo?.dtFinal != null ? { dtInicial: previaInfo.dtInicial, dtFinal: previaInfo.dtFinal } : null),
        };
        setDadosAgrupados(payload);

        // ✅ Quando há filtro de descrição, extrair contas dos grupos retornados
        if (filtroDescricaoFinal) {
          const contasSet = new Set(
            (resultado.grupos || []).map(grupo => normalizarConta(grupo.contaContabil))
          );
          setContasComFiltro(contasSet);
        }
      } else {
        setObjSnackBar({
          type: "error",
          msg: resultado?.mensagem || "Erro ao carregar dados agrupados.",
        });
      }
    } catch (error) {
      console.error('❌ [SobDespesas] Erro ao carregar dados agrupados:', error);
      setObjSnackBar({
        type: "error",
        msg:
          error.response?.data?.mensagem ||
          "Erro ao carregar dados do motor.",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleRecarregarSaldos = useCallback(async () => {
    if (versaoIdInvalido) return;
    try {
      await carregarDadosAgrupados();
      setObjSnackBar({ type: "success", msg: "Saldos e contas atualizados." });
    } catch (err) {
      console.error("Erro ao recarregar saldos:", err);
      setObjSnackBar({
        type: "error",
        msg: err.response?.data?.mensagem || "Erro ao recarregar saldos.",
      });
    }
  }, [versaoIdInvalido]);

  const carregarBasesLegais = useCallback(async () => {
    setLoadingBasesLegais(true);
    try {
      const resultado = await buscarPlanTribBaseLegal();
      const itens = Array.isArray(resultado?.itens)
        ? resultado.itens
        : Array.isArray(resultado)
        ? resultado
        : [];
      setBasesLegais(itens);
    } catch (error) {
      console.error("Erro ao carregar bases legais:", error);
      setBasesLegais([]);
    } finally {
      setLoadingBasesLegais(false);
    }
  }, []);

  useEffect(() => {
    if (versaoIdInvalido) {
      console.error('❌ [SobDespesas] ID da versão não encontrado');
      setObjSnackBar({
        type: "error",
        msg: "Informe uma versão pela lista de prévias ou acesse pelo link correto (Analisar > Sob Despesa). Redirecionando...",
      });
      setTimeout(() => {
        navigate("/piscofins/previas");
      }, 2000);
      return;
    }

    // Carregar dados diretamente - a API busca o CNPJ matriz internamente
    carregarDadosAgrupados();
  }, [versaoIdFinal, scriptFinal]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    carregarBasesLegais();
  }, [carregarBasesLegais]);

  // Buscar dados da prévia/versão pela API (fonte de verdade por versaoId da URL)
  useEffect(() => {
    const buscarDadosPrevia = async () => {
      if (versaoIdInvalido) return;

      try {
        const response = await buscarPreviaVersaoPisCofins(versaoIdFinal);
        const previa = response?.previa ?? response;
        if (previa) {
          setDadosPreviaVersaoApi(previa);
          setPreviaInfo(previa);
        } else {
          setDadosPreviaVersaoApi(null);
        }
      } catch (error) {
        console.error('Erro ao buscar dados da prévia:', error);
        setDadosPreviaVersaoApi(null);
      }
    };

    buscarDadosPrevia();
  }, [versaoIdFinal, setPreviaInfo]);

  // Wrapper para função de atualização (necessário para o hook de fila). Uma requisição por conta (schema_empresa + id_conta_contabil).
  const atualizarGrupoMotorWrapper = useCallback(async (grupo, baseLegalId) => {
    if (versaoIdInvalido) {
      return { sucesso: false, mensagem: 'Versão não disponível.' };
    }
    const schemaEmpresa = grupo?.schema_empresa;
    const idContaContabil = grupo?.id_conta_contabil;
    if (schemaEmpresa == null || idContaContabil == null) {
      return { sucesso: false, mensagem: 'Dados da conta incompletos (schema_empresa ou id_conta_contabil). Recarregue a página.' };
    }
    const body = {
      schema_empresa: schemaEmpresa,
      id_conta_contabil: idContaContabil,
      id_versao: versaoIdFinal,
      base_legal_id: baseLegalId ?? null,
    };
    const resultado = await atualizarDetalhesConta(idContaContabil, body);
    if (resultado?.sucesso) {
      return { ...resultado, totais: resultado?.totais };
    }
    return resultado;
  }, [versaoIdFinal, versaoIdInvalido]);

  // Hook de fila sequencial para evitar race conditions
  const { adicionarFila } = useFilaAtualizacoesGrupos(atualizarGrupoMotorWrapper);

  const handleAtualizarGrupo = async (grupo, baseLegalId) => {
    if (versaoIdInvalido || !scriptFinal) return;

    // Guard clause para versão bloqueada
    if (isBloqueada) {
      setObjSnackBar({
        type: "warning",
        msg: "Não é possível editar. Esta versão está em análise ou aprovada.",
      });
      return;
    }

    const contaContabil = grupo?.contaContabil ?? grupo;
    const schemaEmpresa = grupo?.schema_empresa;
    const idContaContabil = grupo?.id_conta_contabil;
    if (typeof contaContabil !== 'string') {
      setObjSnackBar({ type: "error", msg: "Dados do grupo inválidos." });
      return;
    }

    // Chave única do grupo (schema + id_conta) para não afetar outros grupos com o mesmo código de conta
    const chaveGrupo = `${schemaEmpresa ?? ''}|${idContaContabil ?? ''}`;

    // Marcar grupo como salvando
    setGruposSalvando(prev => new Set(prev).add(chaveGrupo));

    try {
      // Usa fila sequencial para evitar race conditions (uma requisição por conta)
      const resultado = await adicionarFila(grupo, baseLegalId);

      if (resultado?.sucesso) {
        if (resultado.linhasAtualizadas === 0) {
          setObjSnackBar({
            type: "warning",
            msg: resultado?.mensagem || "Nenhum lançamento foi atualizado para esta conta.",
          });
        } else {
          setObjSnackBar({
            type: "success",
            msg: "Grupo atualizado com sucesso!",
          });
        }

        // ✅ Atualizar APENAS o grupo clicado (identificado por schema_empresa + id_conta_contabil).
        // Comparação estrita e normalizada (String/Number) para evitar que várias contas sejam marcadas
        // quando a API devolve tipos diferentes (ex.: id_conta_contabil number vs string) ou valores vazios.
        setDadosAgrupados(prev => {
          if (!prev?.grupos) return prev;

          const schemaNorm = schemaEmpresa != null && String(schemaEmpresa).trim() !== '' ? String(schemaEmpresa).trim() : null;
          const idNorm = idContaContabil != null && Number(idContaContabil) >= 1 ? Number(idContaContabil) : null;
          if (schemaNorm == null || idNorm == null) return prev;

          const gruposAtualizados = prev.grupos.map((g) => {
            const gSchema = g.schema_empresa != null && String(g.schema_empresa).trim() !== '' ? String(g.schema_empresa).trim() : null;
            const gId = g.id_conta_contabil != null && Number(g.id_conta_contabil) >= 1 ? Number(g.id_conta_contabil) : null;
            const mesmoGrupo = gSchema !== null && gId !== null && gSchema === schemaNorm && gId === idNorm;
            if (!mesmoGrupo) return g;
            return {
              ...g,
              baseLegalId: baseLegalId ?? null,
              compoeProjeto: baseLegalId != null,
            };
          });

          return {
            ...prev,
            grupos: gruposAtualizados,
            totais: resultado.totais ?? prev.totais,
          };
        });

        // ✅ Atualizar lançamentos do drill down do grupo (chave = schema_empresa|id_conta_contabil)
        const chaveGrupo = schemaEmpresa != null && idContaContabil != null ? `${schemaEmpresa}|${idContaContabil}` : null;
        if (chaveGrupo) {
          setLancamentosDetalhados(prev => {
            const dados = prev[chaveGrupo];
            if (!dados) return prev;

            const compoe = baseLegalId != null;
            const lancamentosAtualizados = dados.lancamentos.map(l => ({
              ...l,
              compoeProjeto: compoe,
              "Compõe Projeto__boolean": compoe,
              baseLegalId: baseLegalId ?? null,
            }));

            return {
              ...prev,
              [chaveGrupo]: {
                ...dados,
                lancamentos: lancamentosAtualizados
              }
            };
          });
        }
      } else {
        setObjSnackBar({
          type: "error",
          msg: resultado?.mensagem || "Erro ao atualizar grupo.",
        });
      }
    } catch (error) {
      console.error("Erro ao atualizar grupo:", error);
      const mensagemErro = error.response?.data?.mensagem 
        || error.message 
        || "Erro ao atualizar grupo do motor. Verifique se todos os lançamentos foram atualizados corretamente.";
      setObjSnackBar({
        type: "error",
        msg: mensagemErro,
      });
    } finally {
      // Remover grupo do Set de salvando (usar chave única do grupo)
      setGruposSalvando(prev => {
        const novo = new Set(prev);
        novo.delete(chaveGrupo);
        return novo;
      });
    }
  };

  // Chave única do grupo (schema + id) para drill-down por schema+conta
  const getChaveGrupo = useCallback((grupo) => {
    if (grupo?.schema_empresa != null && grupo?.id_conta_contabil != null) {
      return `${grupo.schema_empresa}|${grupo.id_conta_contabil}`;
    }
    return grupo?.contaContabil ?? '';
  }, []);

  // ✅ Função para buscar lançamentos detalhados (drill down por schema + id_conta_contabil)
  const buscarLancamentosDetalhados = useCallback(async (grupo, pagina = 1) => {
    const previaId = dadosVersao?.idPreviaPisCofins ?? previaInfo?.idPreviaPisCofins ?? previaInfo?.idPrevia;
    if (versaoIdInvalido || !previaId || !grupo) return;

    const idContaContabil = grupo.id_conta_contabil ?? grupo.idContaContabil;
    const idContaNum = Number(idContaContabil);
    if (idContaContabil == null || idContaContabil === '' || !Number.isInteger(idContaNum) || idContaNum < 1) {
      console.warn('[CONCILIACAO_SPED_LANCAMENTOS] Grupo sem id_conta_contabil válido; não é possível buscar lançamentos.', grupo);
      return;
    }

    const chaveGrupo = getChaveGrupo(grupo);
    const schemaEmpresa = grupo.schema_empresa ?? null;
    if (!schemaEmpresa || typeof schemaEmpresa !== 'string' || schemaEmpresa.trim() === '') {
      console.warn('[CONCILIACAO_SPED_LANCAMENTOS] Grupo sem schema_empresa; não é possível buscar lançamentos.', grupo);
      return;
    }
    const contaContabil = grupo.contaContabil ?? grupo.conta_contabil ?? normalizarConta(grupo.codigoEcd ?? grupo.codigo_ecd ?? grupo.codigo_contrib);
    const limite = 10;

    setCarregandoLancamentos(prev => new Set(prev).add(chaveGrupo));

    try {
      const resultado = await buscarConciliacaoSpedLancamentos(
        previaId,
        idContaNum,
        versaoIdFinal,
        pagina,
        limite,
        schemaEmpresa.trim()
      );

      if (resultado?.sucesso && resultado?.linhas) {
        const total = resultado.paginacao?.total ?? resultado.linhas.length;
        const limiteResposta = resultado.paginacao?.limite ?? 10;
        const temMais = limiteResposta > 0 && (pagina * limiteResposta) < total;
        setLancamentosDetalhados(prev => {
          const dadosAtuais = prev[chaveGrupo] || { lancamentos: [], pagina: 0, total: 0 };
          const idContaContabil = grupo.id_conta_contabil ?? grupo.idContaContabil;

          if (pagina === 1) {
            const novoEstado = {
              ...prev,
              [chaveGrupo]: {
                lancamentos: resultado.linhas,
                pagina,
                temMais,
                total,
                schema_empresa: schemaEmpresa,
                contaContabil,
                id_conta_contabil: idContaContabil,
              },
            };
            lancamentosDetalhadosRef.current = novoEstado;
            return novoEstado;
          }
          const novoEstado = {
            ...prev,
            [chaveGrupo]: {
              lancamentos: [...dadosAtuais.lancamentos, ...resultado.linhas],
              pagina,
              temMais,
              total,
              schema_empresa: dadosAtuais.schema_empresa ?? schemaEmpresa,
              contaContabil: dadosAtuais.contaContabil ?? contaContabil,
              id_conta_contabil: dadosAtuais.id_conta_contabil ?? idContaContabil,
            },
          };
          lancamentosDetalhadosRef.current = novoEstado;
          return novoEstado;
        });
      }
    } catch (error) {
      console.error('❌ Erro ao buscar lançamentos detalhados:', error);
      setObjSnackBar({
        type: "error",
        msg: "Erro ao carregar lançamentos detalhados.",
      });
    } finally {
      setCarregandoLancamentos(prev => {
        const novo = new Set(prev);
        novo.delete(chaveGrupo);
        return novo;
      });
    }
  }, [versaoIdFinal, getChaveGrupo, normalizarConta, dadosVersao?.idPreviaPisCofins, previaInfo?.idPreviaPisCofins, previaInfo?.idPrevia]);

  // ✅ Função para carregar próxima página (scroll infinito) — chaveGrupo = schema_empresa|id_conta_contabil
  const carregarProximaPagina = useCallback(async (chaveGrupo) => {
    const dados = lancamentosDetalhados[chaveGrupo];
    if (!dados || !dados.temMais || carregandoLancamentos.has(chaveGrupo)) {
      return;
    }
    const proximaPagina = dados.pagina + 1;
    const grupo = {
      schema_empresa: dados.schema_empresa,
      id_conta_contabil: dados.id_conta_contabil,
      contaContabil: dados.contaContabil,
    };
    await buscarLancamentosDetalhados(grupo, proximaPagina);
  }, [lancamentosDetalhados, carregandoLancamentos, buscarLancamentosDetalhados]);

  // ✅ Função para buscar lançamentos por descrição - AGORA SIMPLIFICADA (backend faz o trabalho)
  const buscarLancamentosPorDescricao = useCallback(async (textoBusca) => {
    const textoBuscaNormalizado = String(textoBusca || "").trim();
    
    if (versaoIdInvalido || !scriptFinal || !textoBuscaNormalizado) {
      setContasComFiltro(new Set());
      buscandoPorDescricaoRef.current = false;
      ultimaBuscaDescricaoRef.current = "";
      // ✅ Recarregar dados sem filtro (parâmetros explícitos para não usar estado)
      await carregarDadosAgrupados(null, null, null);
      return;
    }

    // Evitar múltiplas buscas simultâneas ou busca duplicada
    if (buscandoPorDescricaoRef.current || ultimaBuscaDescricaoRef.current === textoBuscaNormalizado) {
      return;
    }

    try {
      buscandoPorDescricaoRef.current = true;
      ultimaBuscaDescricaoRef.current = textoBuscaNormalizado;
      
      await carregarDadosAgrupados(textoBuscaNormalizado, null, null, 1, limitePorPagina);
      
    } catch (error) {
      console.error('Erro ao buscar lançamentos por descrição:', error);
      setObjSnackBar({
        type: "error",
        msg: "Erro ao buscar lançamentos por descrição.",
      });
      setContasComFiltro(new Set());
      ultimaBuscaDescricaoRef.current = ""; // Reset em caso de erro
    } finally {
      buscandoPorDescricaoRef.current = false;
    }
  }, [versaoIdFinal, scriptFinal, limitePorPagina, carregarDadosAgrupados]);

  // ✅ Função para atualizar linha individual (usa PATCH /conciliacao_sped/detalhes/lancamento)
  const handleAtualizarLinha = useCallback(async (linha, baseLegalId) => {
    if (versaoIdInvalido || !scriptFinal) return;
    if (linha?.__id_lancamento == null || !linha?.schemaEmpresa) {
      setObjSnackBar({
        type: "error",
        msg: "Dados do lançamento incompletos (id ou schema). Abra novamente os detalhes da conta.",
      });
      return;
    }

    // Guard clause para versão bloqueada
    if (isBloqueada) {
      setObjSnackBar({
        type: "warning",
        msg: "Não é possível editar. Esta versão está em análise ou aprovada.",
      });
      return;
    }

    const schemaLinha = linha.schema_empresa ?? linha.schemaEmpresa;
    const idContaLinha = linha.id_conta_contabil ?? linha.idContaContabil;
    if (schemaLinha == null || idContaLinha == null || String(schemaLinha).trim() === '') {
      setObjSnackBar({
        type: "error",
        msg: "Dados do lançamento incompletos (conta/schema). Abra novamente os detalhes da conta.",
      });
      return;
    }

    // Mesmo formato que getChaveGrupo / chaves em lancamentosDetalhados (sem trim na chave)
    const chaveGrupoLinha = `${schemaLinha}|${idContaLinha}`;

    setContaExpandida(null);
    setLancamentosDetalhados((prev) => {
      const next = { ...prev };
      delete next[chaveGrupoLinha];
      lancamentosDetalhadosRef.current = next;
      return next;
    });

    setGruposSalvando((prev) => new Set(prev).add(chaveGrupoLinha));

    try {
      const resultado = await atualizarDetalheLancamento({
        id_lancamento: linha.__id_lancamento,
        id_versao: versaoIdFinal,
        schema_empresa: linha.schemaEmpresa,
        base_legal_id: baseLegalId ?? null,
      });

      if (resultado?.sucesso) {
        // Atualizar totais dos cards: usar totais retornados pelo PATCH (API já atualizou tabela de resumo)
        if (resultado?.totais) {
          setDadosAgrupados((prev) => (prev ? { ...prev, totais: resultado.totais } : prev));
        } else {
          try {
            const resTotais = await buscarTotaisCompoeConciliacaoSped(versaoIdFinal);
            if (resTotais?.sucesso && resTotais?.totais) {
              setDadosAgrupados((prev) => (prev ? { ...prev, totais: resTotais.totais } : prev));
            }
          } catch (errTotais) {
            console.error('Erro ao recalcular totais:', errTotais);
          }
        }

        setObjSnackBar({
          type: "success",
          msg: "Lançamento atualizado com sucesso!",
        });
      }
    } catch (error) {
      console.error('❌ Erro ao atualizar linha:', error);
      setObjSnackBar({
        type: "error",
        msg: "Erro ao atualizar lançamento.",
      });
    } finally {
      setGruposSalvando((prev) => {
        const novo = new Set(prev);
        novo.delete(chaveGrupoLinha);
        return novo;
      });
    }
  }, [versaoIdFinal, scriptFinal, isBloqueada]);

  // ✅ Função para toggle de expansão (drill down por schema + conta)
  const handleToggleExpand = useCallback(async (grupo) => {
    const chaveGrupo = getChaveGrupo(grupo);
    const precisaFechar = contaExpandida === chaveGrupo;

    if (precisaFechar) {
      setContaExpandida(null);
      return;
    }

    setContaExpandida(chaveGrupo);

    if (!lancamentosDetalhadosRef.current[chaveGrupo]) {
      buscarLancamentosDetalhados(grupo, 1).catch(err => {
        console.error('Erro ao buscar lançamentos:', err);
      });
    }
  }, [contaExpandida, getChaveGrupo, buscarLancamentosDetalhados]);

  const grupos = dadosAgrupados?.grupos || [];

  const salvandoGrupoComListaFechada = useMemo(
    () => !contaExpandida && gruposSalvando.size > 0,
    [contaExpandida, gruposSalvando],
  );

  const uiBloqueadaSalvamento = loading || salvandoGrupoComListaFechada;

  // Filtrar grupos baseado no filtro selecionado
  const gruposFiltrados = useMemo(() => {
    if (!colunaFiltro || !valorFiltro) {
      // ❌ CORREÇÃO CRÍTICA: Removido setState dentro de useMemo - isso causa loops infinitos!
      // A limpeza de filtros deve ser feita no useEffect, não aqui
      return grupos;
    }

    // ✅ Filtros feitos no backend: retornar grupos diretamente (backend já filtrou)
    const filtrosBackend = ['descricaoLancamento', 'descricaoConta', 'cnpj'];
    if (filtrosBackend.includes(colunaFiltro)) {
      return grupos;
    }

    // ✅ Filtros que devem ser feitos no frontend (propriedades calculadas dos grupos)
    return grupos.filter((grupo) => {
      switch (colunaFiltro) {

        case "valorTotal":
        case "pis":
        case "cofins":
          // Buscar diretamente no texto formatado como exibido na tabela
          const nomeCampo = colunaFiltro === "valorTotal" ? "valorTotalEcd" : colunaFiltro;
          const valorGrupo = grupo[nomeCampo];
          
          // Validar se o campo existe e é um número válido
          if (valorGrupo === null || valorGrupo === undefined) {
            return false;
          }
          
          // Formatar o valor da mesma forma que é exibido na tabela
          const valorComoNumero = typeof valorGrupo === 'number' ? valorGrupo : parseFloat(String(valorGrupo || 0));
          
          if (isNaN(valorComoNumero)) {
            return false;
          }
          
          // Formatar exatamente como é exibido na tela (R$ X.XXX,XX)
          const valorFormatado = new Intl.NumberFormat("pt-BR", {
            style: "currency",
            currency: "BRL",
          }).format(valorComoNumero);
          
          // Normalizar filtro: remover espaços e converter para lowercase para busca case-insensitive
          const filtroNormalizado = String(valorFiltro || "").trim();
          
          if (!filtroNormalizado) return true;
          
          // Buscar o filtro diretamente na string formatada (case-insensitive)
          return valorFormatado.toLowerCase().includes(filtroNormalizado.toLowerCase());

        case "compoeProjeto":
          if (valorFiltro === null) return true; // "Todos"
          return grupo.compoeProjeto === valorFiltro;

        case "baseLegal":
          return grupo.baseLegalId === Number(valorFiltro);

        case "codigoEcd": {
          const codigoEcdGrupo = String(grupo.codigoEcd ?? "").trim();
          const filtroCodigoEcd = String(valorFiltro ?? "").trim();
          if (!filtroCodigoEcd) return true;
          return codigoEcdGrupo.toLowerCase().includes(filtroCodigoEcd.toLowerCase());
        }

        default:
          return true;
      }
    });
  }, [grupos, colunaFiltro, valorFiltro, normalizarConta]);

  // ✅ Efeito apenas para recarregar quando filtros forem REMOVIDOS (aplicação de filtro é feita no onFiltrar)
  useEffect(() => {
    const filtrosBackendColunas = ['descricaoLancamento', 'descricaoConta', 'cnpj', 'codigoEcd', 'tipoContaContabil', 'compoeProjeto'];
    const valorFiltroNormalizado = String(valorFiltro ?? "").trim();
    const temFiltroBackend = filtrosBackendColunas.includes(colunaFiltro) && (valorFiltroNormalizado !== '' || valorFiltro === true || valorFiltro === false);

    if (!temFiltroBackend) {
      // Recarregar sem filtro apenas se havia filtro de backend antes (evita dupla recarga com onLimpar)
      if (ultimaBuscaDescricaoRef.current !== "") {
        ultimaBuscaDescricaoRef.current = "";
        carregarDadosAgrupados(null, null, null);
        setContasComFiltro(new Set());
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [colunaFiltro, valorFiltro]);

  const handleAvançar = () => {
    if (versaoIdInvalido) return;
    navigate(`/piscofins/previas/${versaoIdFinal}/base-legal`);
  };

  // Priorizar dados da API (por versaoId da URL) para evitar exibir empresa/CNPJ de outra prévia
  const empresaNome =
    dadosPreviaVersaoApi?.razaoSocial ||
    dadosPreviaVersaoApi?.empresa ||
    dadosAgrupados?.empresa ||
    dadosVersao?.nomeEmpresa ||
    dadosVersao?.empresa ||
    dadosVersao?.razaoSocial ||
    previaInfo?.razaoSocial ||
    previaInfo?.empresa ||
    "Empresa não informada";
  const cnpjEmpresa = formatarCnpj(
    dadosPreviaVersaoApi?.cnpj ??
    dadosPreviaVersaoApi?.cnpjMatriz ??
    dadosAgrupados?.cnpjEmpresa ??
    dadosVersao?.cnpjMatriz ??
    dadosVersao?.cnpj ??
    previaInfo?.cnpj ??
    ""
  );
  
  // Extrair período: preferir dados da resposta; se vazio ou inexistente (ex.: filtro), usar dados da versão e prévia
  const periodoResposta = dadosAgrupados?.periodo;
  const periodoVersao = dadosVersao?.periodo;
  const periodoPrevia = previaInfo?.periodo;
  const periodoComDatas =
    periodoResposta && (periodoResposta.dtInicial || periodoResposta.dtFinal || periodoResposta.inicio || periodoResposta.fim)
      ? periodoResposta
      : periodoVersao && (periodoVersao.dtInicial || periodoVersao.dtFinal || periodoVersao.inicio || periodoVersao.fim)
        ? periodoVersao
        : periodoPrevia && (periodoPrevia.dtInicial || periodoPrevia.dtFinal || periodoPrevia.inicio || periodoPrevia.fim)
          ? periodoPrevia
          : null;
  const periodo = periodoComDatas || periodoResposta || periodoVersao || periodoPrevia || null;
  const dtInicial = periodo?.dtInicial || periodo?.inicio || dadosVersao?.dtInicial || previaInfo?.dtInicial || null;
  const dtFinal = periodo?.dtFinal || periodo?.fim || dadosVersao?.dtFinal || previaInfo?.dtFinal || null;
  const dtInicialFormatada = formatValue("data", dtInicial) || "";
  const dtFinalFormatada = formatValue("data", dtFinal) || "";
  const periodoFormatado = dtInicialFormatada && dtFinalFormatada
    ? `${dtInicialFormatada} a ${dtFinalFormatada}`
    : dtInicialFormatada || dtFinalFormatada || "Período não informado";
  
  const temasSelecionados = Array.isArray(dadosVersao?.temasSelecionados)
    ? dadosVersao.temasSelecionados.join(" • ")
    : dadosVersao?.temaSelecionado || "Sob Despesas";

  // Handler para voltar à lista de prévias já com a modal de versões da PRÉVIA aberta
  const handleVoltarParaLista = () => {
    const idPreviaPisCofins =
      dadosPreviaVersaoApi?.idPreviaPisCofins ??
      dadosPreviaVersaoApi?.idPrevia ??
      dadosVersao?.idPreviaPisCofins ??
      previaInfo?.idPreviaPisCofins ??
      previaInfo?.idPrevia ??
      null;

    if (idPreviaPisCofins && typeof voltarParaListaComModalVersoes === "function") {
      const dadosEmpresa = {
        idPreviaPisCofins,
        razaoSocial:
          dadosPreviaVersaoApi?.razaoSocial ||
          dadosPreviaVersaoApi?.empresa ||
          dadosVersao?.nomeEmpresa ||
          dadosAgrupados?.empresa ||
          previaInfo?.razaoSocial ||
          "Empresa não informada",
        cnpj:
          dadosPreviaVersaoApi?.cnpj ??
          dadosPreviaVersaoApi?.cnpjMatriz ??
          dadosAgrupados?.cnpjEmpresa ??
          dadosVersao?.cnpjMatriz ??
          dadosVersao?.cnpj ??
          previaInfo?.cnpj ??
          "",
        dtInicial:
          dadosVersao?.periodo?.dtInicial ||
          dadosVersao?.dtInicial ||
          dadosPreviaVersaoApi?.dtInicial ||
          previaInfo?.dtInicial ||
          null,
        dtFinal:
          dadosVersao?.periodo?.dtFinal ||
          dadosVersao?.dtFinal ||
          dadosPreviaVersaoApi?.dtFinal ||
          previaInfo?.dtFinal ||
          null,
        status: previaInfo?.status ?? dadosPreviaVersaoApi?.status,
      };

      voltarParaListaComModalVersoes({
        idPreviaPisCofins,
        dadosEmpresa,
      });
    } else {
      // Fallback seguro: apenas voltar para a lista
      navigate("/piscofins/previas");
    }
  };

  const handleExportar = async () => {
    if (versaoIdInvalido || !versaoIdFinal) {
      setObjSnackBar({
        type: "error",
        msg: "Dados da versão não disponíveis para exportar.",
      });
      return;
    }

    setLoading(true);
    try {
      const res = await exportarCsvPreviaPisCofins(versaoIdFinal);
      if (res?.nomeArquivo) {
        setObjSnackBar({
          type: "success",
          msg: `Arquivo ${res.nomeArquivo} salvo em Relatórios com sucesso!`,
        });
      } else {
        setObjSnackBar({
          type: "info",
          msg: res?.mensagem || "Nenhum lançamento com \"compõe projeto\" para esta versão. Arquivo não gerado.",
        });
      }
    } catch (error) {
      console.error("Erro ao exportar CSV:", error);
      setObjSnackBar({
        type: "error",
        msg: error?.response?.data?.error || error?.message || "Erro ao exportar CSV. Por favor, tente novamente.",
      });
    } finally {
      setLoading(false);
    }
  };

  if (loading && !dadosAgrupados) {
    return <Loading loading={true} />;
  }

  return (
    <div className={styles.page}>
      <div className={styles.card}>

        <div className={styles.infoGrid}>
          <div className={styles.infoItem}>
            <span>Empresa</span>
            <strong>{empresaNome}</strong>
          </div>
          <div className={styles.infoItem}>
            <span>CNPJ</span>
            <strong>{cnpjEmpresa}</strong>
          </div>
          <div className={styles.infoItem}>
            <span>Período</span>
            <strong>{periodoFormatado}</strong>
          </div>
          <div className={styles.infoItem}>
            <span>Temas selecionados</span>
            <strong>{temasSelecionados}</strong>
          </div>
        </div>

        <StepsIndicator activeStep={1} />

        {totais && (
          <div className={styles.totalsRow}>
            <div className={`${styles.totalCard} ${styles.totalEcd}`}>
              <button
                type="button"
                onClick={handleRecarregarSaldos}
                className={styles.totalCardReload}
                disabled={uiBloqueadaSalvamento}
                title="Recarregar saldos"
                aria-label="Recarregar saldos"
              >
                <RefreshIcon className={styles.iconRecarregarCard} />
              </button>
              <span>Total ECD</span>
              <strong>{formatCurrency(totais.valorTotalEcd)}</strong>
            </div>
            <div className={`${styles.totalCard} ${styles.totalPis}`}>
              <button
                type="button"
                onClick={handleRecarregarSaldos}
                className={styles.totalCardReload}
                disabled={uiBloqueadaSalvamento}
                title="Recarregar saldos"
                aria-label="Recarregar saldos"
              >
                <RefreshIcon className={styles.iconRecarregarCard} />
              </button>
              <span>Total PIS (1,65%)</span>
              <strong>{formatCurrency(totais.pis)}</strong>
            </div>
            <div className={`${styles.totalCard} ${styles.totalCofins}`}>
              <button
                type="button"
                onClick={handleRecarregarSaldos}
                className={styles.totalCardReload}
                disabled={uiBloqueadaSalvamento}
                title="Recarregar saldos"
                aria-label="Recarregar saldos"
              >
                <RefreshIcon className={styles.iconRecarregarCard} />
              </button>
              <span>Total COFINS (7,6%)</span>
              <strong>{formatCurrency(totais.cofins)}</strong>
            </div>
          </div>
        )}

        {dadosAgrupados && (
                    <>
                          <>
                            <div className={styles.filtrosContainer}>
                              <FiltroTabelaPisCofins
                                colunaSelecionada={colunaFiltro}
                                valorFiltro={valorFiltro}
                                basesLegais={basesLegais}
                                onColunaChange={setColunaFiltro}
                                onValorChange={setValorFiltro}
                                onFiltrar={(coluna, valor) => {
                                  const valorTexto = (valor !== undefined && valor !== null && String(valor).trim() !== '') ? String(valor).trim() : null;
                                  setColunaFiltro(coluna);
                                  setValorFiltro(valor ?? '');
                                  setPaginaAtual(1);
                                  const filtrosBackend = ['descricaoLancamento', 'descricaoConta', 'cnpj', 'codigoEcd', 'tipoContaContabil', 'compoeProjeto'];
                                  if (coluna === 'descricaoLancamento') {
                                    if (valorTexto) ultimaBuscaDescricaoRef.current = valorTexto;
                                    carregarDadosAgrupados(valorTexto ?? undefined, null, null);
                                  } else if (coluna === 'descricaoConta') {
                                    carregarDadosAgrupados(null, 'descricaoConta', valorTexto ?? undefined);
                                  } else if (coluna === 'cnpj') {
                                    carregarDadosAgrupados(null, 'cnpj', valorTexto ?? valor ?? undefined);
                                  }
                                  // Demais colunas: filtro só no frontend (gruposFiltrados)
                                }}
                                onLimpar={() => {
                                  setColunaFiltro("");
                                  setValorFiltro("");
                                  ultimaBuscaDescricaoRef.current = "";
                                  setContasComFiltro(new Set());
                                  carregarDadosAgrupados(null, null, null);
                                }}
                                loading={uiBloqueadaSalvamento}
                              />
                              {pode(CHAVES_PIS_COFINS_EXPORT) && (
                              <div className={styles.filtrosRight}>
                                <button
                                  type="button"
                                  onClick={handleExportar}
                                  className={styles.botaoExportar}
                                  disabled={uiBloqueadaSalvamento}
                                >
                                  <DownloadIcon style={{ fontSize: "1rem", marginRight: "0.5rem" }} />
                                  <span>Exportar</span>
                                </button>
                              </div>
                              )}
                            </div>

                {colunaFiltro === "descricaoLancamento" &&
                  String(valorFiltro || "").trim() &&
                  !loading &&
                  (grupos.length === 0) && (
                    <div className={styles.avisoFiltroVazio} role="status">
                      Nenhum lançamento encontrado com a descrição informada. Tente outro texto ou limpe o filtro.
                    </div>
                )}

                <TabelaGrupos
                  grupos={grupos}
                  onAtualizarGrupo={handleAtualizarGrupo}
                  loading={loading}
                  gruposSalvando={gruposSalvando}
                  basesLegais={basesLegais}
                  loadingBasesLegais={loadingBasesLegais}
                  footerInfo={null}
                  paginacao={null}
                  lancamentosFiltrados={lancamentosFiltrados}
                  isBloqueada={isBloqueada}
                  bloqueioSalvamentoGrupoListaFechada={salvandoGrupoComListaFechada}
                  // ✅ PROPS PARA DRILL DOWN
                  contaExpandida={contaExpandida}
                  onToggleExpand={handleToggleExpand}
                  lancamentosDetalhados={lancamentosDetalhados}
                  carregandoLancamentos={carregandoLancamentos}
                  onAtualizarLinha={handleAtualizarLinha}
                  onCarregarMais={carregarProximaPagina}
                  colunaFiltro={colunaFiltro}
                  versaoIdPagina={versaoIdFinal}
                />

                <div className={styles.footer}>
                  <div className={styles.footerLeft}>
                    <WhiteButton
                      textValue="Voltar"
                    clickFunction={handleVoltarParaLista}
                    />
                  </div>

                  <div className={styles.paginationWrapper}>
                    <Pagination
                      serverSideTotal={dadosAgrupados?.paginacao?.total ?? 0}
                      serverSidePage={paginaAtual}
                      serverSideRowsPerPage={limitePorPagina}
                      onServerPageChange={(novaPagina) => {
                        setPaginaAtual(novaPagina);
                        carregarDadosAgrupados(undefined, undefined, undefined, novaPagina, limitePorPagina);
                      }}
                      onServerRowsPerPageChange={(novoLimite) => {
                        setLimitePorPagina(novoLimite);
                        setPaginaAtual(1);
                        carregarDadosAgrupados(undefined, undefined, undefined, 1, novoLimite);
                      }}
                      paginationKey="sob-despesas-grupos"
                      disabled={salvandoGrupoComListaFechada}
                    />
                  </div>

                  <div className={styles.footerRight}>
                    {pode(CHAVES_PIS_COFINS_EDIT) && (
                    <BlueButton 
                      textValue="Avançar" 
                      clickFunction={handleAvançar}
                      disabled={uiBloqueadaSalvamento}
                    />
                    )}
                  </div>
                </div>
              </>
          </>
        )}
      </div>

      <SnackBar objSnackBar={objSnackBar} setObjSnackBar={setObjSnackBar} />
    </div>
  );
};

export default SobDespesas;

